import { useEffect, useState, useRef, memo } from "react";
import axios from "axios";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";
import { MdFileDownload } from "react-icons/md";
import { IoSearch } from "react-icons/io5";
import { toast } from "react-hot-toast";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import Modal from "react-modal";
import trashIcon from "../assets/icon/trash.svg";
import backarrow from "../assets/icon/arrow-right.svg";
import {
  FaBook,
  FaBookOpen,
  FaRegListAlt,
  FaDatabase,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaCalculator,
  FaChartLine,
  FaCoins,
  FaPenFancy,
  FaRegFileAlt,
  FaPencilAlt,
} from "react-icons/fa";

const AnimatedBackground = memo(() => {
  const icons = [
    FaBook,
    FaBookOpen,
    FaRegListAlt,
    FaDatabase,
    FaUserGraduate,
    FaChalkboardTeacher,
    FaCalculator,
    FaChartLine,
    FaCoins,
    FaPenFancy,
    FaRegFileAlt,
    FaPencilAlt,
  ];

  const rows = 5;
  const cols = 11;
  const iconArray = [];

  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      const Icon = icons[Math.floor(Math.random() * icons.length)];
      iconArray.push({
        Icon,
        size: 18 + Math.random() * 20,
        top: (i / rows) * 100 + Math.random() * 5,
        left: (j / cols) * 100 + Math.random() * 5,
        duration: 15 + Math.random() * 10,
        opacity: 0.15 + Math.random() * 0.25,
        xOffset: Math.random() * 10 - 5,
        yOffset: Math.random() * 10 - 5,
      });
    }
  }

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {iconArray.map((item, i) => (
        <motion.div
          key={i}
          className="absolute text-white/50"
          style={{
            top: `${item.top}%`,
            left: `${item.left}%`,
            opacity: item.opacity,
          }}
          initial={{ scale: 0.5 }}
          animate={{
            x: [0, item.xOffset, 0],
            y: [0, item.yOffset, 0],
            scale: [0.5, 1, 0.5],
          }}
          transition={{
            duration: item.duration,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <item.Icon size={item.size} />
        </motion.div>
      ))}
    </div>
  );
});

const Attendance = () => {
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL;

  const [courses, setCourses] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  const searchRef = useRef("");
  const debounceRef = useRef(null);

  const fetchCourses = async (pageNum = 1) => {
    setLoading(true);
    const limit = window.innerHeight < 1000 ? 6 : 9;

    try {
      const res = await axios.get(`${API_BASE}/api/course/Get-All-Courses`, {
        params: { search: searchRef.current.trim(), page: pageNum, limit },
        headers: { Authorization: localStorage.getItem("token") },
      });
      setCourses(res.data.data.courses || []);
      setTotalPages(res.data.data.totalPages || 1);
      setPage(pageNum);
    } catch {
      toast.error("خطا در دریافت دوره‌ها");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses(page);
  }, [page]);

  const handleSearch = (value) => {
    searchRef.current = value;
    setPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchCourses(1), 500);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_BASE}/api/Course/Delete-Course/${deleteId}`, {
        headers: { Authorization: localStorage.getItem("token") },
      });
      toast.success("دوره حذف شد");
      setCourses((prev) => prev.filter((c) => c._id !== deleteId));
      if (courses.length === 1 && page > 1) fetchCourses(page - 1);
      else fetchCourses(page);
    } catch (err) {
      toast.error(err.response?.data?.message || "خطا در حذف");
    } finally {
      setModalIsOpen(false);
      setDeleteId("");
    }
  };

  const handleDownload = () => toast.info("دانلود اکسل در حال توسعه");

  const visiblePages = window.innerWidth < 768 ? 2 : 3;
  const half = Math.floor(visiblePages / 2);
  let startPage = Math.max(1, page - half);
  let endPage = Math.min(totalPages, startPage + visiblePages - 1);
  if (endPage - startPage + 1 < visiblePages)
    startPage = Math.max(1, endPage - visiblePages + 1);

  if (!localStorage.getItem("token")) return <Navigate to="/" />;

  const customStyles = {
    content: {
      borderRadius: "16px",
      margin: "auto",
      padding: "0",
      border: "none",
      width: "320px",
      height: "180px",
      background: "white",
    },
    overlay: {
      backgroundColor: "rgba(0,0,0,0.55)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  };

  return (
    <div className="relative min-h-screen w-full bg-gray-800 flex flex-col items-center p-4">
      <AnimatedBackground />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-4xl mx-auto border border-white/30 overflow-hidden mt-8"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <img
            src={backarrow}
            alt="back"
            className="w-8 h-8 cursor-pointer hover:scale-110 transition"
            onClick={() => navigate(-1)}
          />
          <h2 className="text-2xl font-bold text-gray-800">دوره‌ها</h2>
          <div className="w-8" />
        </div>

        {/* Search + Download */}
        <div className="p-6 pt-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative w-full sm:max-w-[250px]">
              <input
                type="text"
                placeholder="فیلتر : نام دوره ..."
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-md border-2 border-gray-300 focus:border-gray-700 outline-none"
              />
              <IoSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
            </div>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-5 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-md"
            >
              <MdFileDownload size={20} /> دانلود اکسل
            </button>
          </div>
        </div>

        {/* Grid Cards */}
        <div className="px-6 pb-10">
          {loading ? (
            <p className="text-center py-12 text-xl font-bold text-gray-600">
              در حال بارگذاری...
            </p>
          ) : courses.length === 0 ? (
            <p className="text-center py-12 text-xl font-bold text-gray-600">
              دوره‌ای یافت نشد!
            </p>
          ) : (
            <>
              <div className="grid xs:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map((course, i) => (
                  <motion.div
                    key={course._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() =>
                      navigate("/Mainpage/AttendanceList", {
                        state: { courseid: course._id },
                      })
                    }
                    className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer border border-gray-200"
                  >
                    <div className="p-4 flex flex-col items-center">
                      {/* عکس دوره - کوچکتر */}
                      <div className="w-20 h-20 rounded-full border-2 border-gray-700 overflow-hidden mb-3">
                        {course.CoursesImage?.fileName ? (
                          <img
                            src={`${API_BASE}/${course.CoursesImage.fileName}`}
                            alt={course.CourseName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-bold">
                            عکس موجود نیست
                          </div>
                        )}
                      </div>

                      {/* نام دوره */}
                      <h3 className="text-lg font-bold text-gray-800 mb-1 text-center">
                        {course.CourseName}
                      </h3>

                      {/* توضیحات */}
                      <p className="text-gray-600 text-center mb-3 text-sm line-clamp-2">
                        {course.CourseDescription || "بدون توضیحات"}
                      </p>

                      <div className="w-full h-px bg-gray-300 my-3" />

                      <p className="text-[#265166] font-semibold text-sm">
                        لیست حضور و غیاب
                      </p>

                      {/* آیکون حذف */}
                      <img
                        src={trashIcon}
                        alt="delete"
                        className="w-5 h-5 mt-3 cursor-pointer opacity-70 hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteId(course._id);
                          setModalIsOpen(true);
                        }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-3 mt-10">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-full disabled:opacity-50"
                  >
                    <FaArrowRight size={20} />
                  </button>
                  {Array.from(
                    { length: endPage - startPage + 1 },
                    (_, i) => startPage + i
                  ).map((num) => (
                    <motion.button
                      key={num}
                      whileHover={{ scale: 1.1 }}
                      onClick={() => setPage(num)}
                      className={`w-8 h-8 rounded-md ${
                        num === page ? "bg-gray-800 text-white" : "bg-gray-200"
                      }`}
                    >
                      {num}
                    </motion.button>
                  ))}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-full disabled:opacity-50"
                  >
                    <FaArrowLeft size={20} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>

      {/* Modal حذف */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        style={customStyles}
      >
        <div className="h-full flex flex-col items-center justify-center text-center">
          <p className="text-lg font-bold mb-6">
            آیا مطمئنید این دوره حذف شود؟
          </p>
          <div className="flex gap-4">
            <button
              onClick={handleDelete}
              className="px-6 py-2 bg-red-600 text-white rounded-lg"
            >
              بله
            </button>
            <button
              onClick={() => setModalIsOpen(false)}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg"
            >
              خیر
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Attendance;
